import React from 'react';

import {
  actions,
  makePropGetter,
  ensurePluginOrder,
  useGetLatest,
  useMountedLayoutEffect,
} from 'react-table';

const pluginName = 'useSingleRowSelect';

// Actions
actions.resetSelectedRows = 'resetSelectedRows';
actions.toggleRowSelected = 'toggleRowSelected';

const defaultGetToggleRowSelectedProps = (props, { instance, row }) => {
  const { manualRowSelectedKey = 'isSelected' } = instance;
  let checked = false;

  if (row.original && row.original[manualRowSelectedKey]) {
    checked = true;
  } else {
    checked = row.isSelected;
  }

  return [
    props,
    {
      onChange: (e) => {
        row.toggleRowSelected(e.target.checked);
      },
      style: {
        cursor: 'pointer',
      },
      checked,
      title: 'Toggle Row Selected',
    },
  ];
};

function reducer(state, action, previousState, instance) {
  if (action.type === actions.init) {
    return {
      selectedRowIds: {},
      ...state,
    };
  }

  if (action.type === actions.resetSelectedRows) {
    return {
      ...state,
      selectedRowIds: instance.initialState.selectedRowIds || {},
    };
  }

  if (action.type === actions.toggleRowSelected) {
    const { id, value } = action;
    const idIsSelected = state.selectedRowIds.hasOwnProperty(id);
    // handle toggling to user specified state
    if (typeof value !== 'undefined') {
      // if value is truthy we set the id as selected
      if (value) {
        return {
          ...state,
          selectedRowIds: { [id]: true },
        };
      } else {
        // if value is falsey we check if the id needs to be unset
        if (idIsSelected) {
          return { ...state, selectedRowIds: {} };
        } else {
          return state;
        }
      }
    }
    // handle toggle off
    if (idIsSelected) {
      return {
        ...state,
        selectedRowIds: {},
      };
    }
    // handle toggle on
    return {
      ...state,
      selectedRowIds: { [id]: true },
    };
  }

  return state;
}

function useInstance(instance) {
  const {
    data,
    rows,
    plugins,
    autoResetSelectedRows = true,
    state: { selectedRowIds },
    dispatch,
  } = instance;

  ensurePluginOrder(
    plugins,
    // mirroring useRowSelect's ordering
    ['useFilters', 'useGroupBy', 'useSortBy', 'useExpanded', 'usePagination'],
    'useSingleRowSelect'
  );

  const selectedFlatRows = React.useMemo(() => {
    const selectedFlatRows = [];

    rows.forEach((row) => {
      const isSelected = !!selectedRowIds[row.id];
      row.isSelected = !!isSelected;

      if (isSelected) {
        selectedFlatRows.push(row);
      }
    });

    return selectedFlatRows;
  }, [rows, selectedRowIds]);

  const getAutoResetSelectedRows = useGetLatest(autoResetSelectedRows);

  useMountedLayoutEffect(() => {
    if (getAutoResetSelectedRows()) {
      dispatch({ type: actions.resetSelectedRows });
    }
  }, [dispatch, data]);

  const toggleRowSelected = React.useCallback(
    (id, value) => dispatch({ type: actions.toggleRowSelected, id, value }),
    [dispatch]
  );

  Object.assign(instance, {
    selectedFlatRows,
    toggleRowSelected,
  });
}

function prepareRow(row, { instance }) {
  row.toggleRowSelected = (set) => instance.toggleRowSelected(row.id, set);

  row.getToggleRowSelectedProps = makePropGetter(
    instance.getHooks().getToggleRowSelectedProps,
    { instance, row }
  );
}

export const useSingleRowSelect = (hooks) => {
  hooks.getToggleRowSelectedProps = [defaultGetToggleRowSelectedProps];
  hooks.stateReducers.push(reducer);
  hooks.useInstance.push(useInstance);
  hooks.prepareRow.push(prepareRow);
};

useSingleRowSelect.pluginName = pluginName;
